import { Localizer } from "infrastructure/localization/localizer";
import FilterTitle from "./filterTitle";
import { useEffect, useMemo, useState } from "react";
import ForSelectDropdown from "core/components/dropdown/components/forSelectDropdown";
import useStamdataApi from "core/hooks/useStamdataApi";
import { TypedFilterWithPermissionsProps } from "../types/typedFilterProps";
import { DirectUserPermissions, mapUserPermissionsToDropdownPermissions } from "core/sharedmodels/userPermissions/userFilterPermissions";
import RegionerMultiDropdown from "core/components/dropdown/components/regionerMultiDropdown";
import { PlaceFilterVisualizationConfig } from "../types/visualizationConfig";

export type PlaceFilterResult = {
    regioner: string[];
    omraader: string[];
    afdelinger: string[],
    praksiser: string[],
    sygehuse: string[],
    allRegionsSelected?: boolean,
}

export type PlaceFilterRestrictModel = Partial<PlaceFilterResult>;

type PlaceFilterProps = {
    preselectViaPermissions?: boolean;
} & TypedFilterWithPermissionsProps<PlaceFilterResult, PlaceFilterRestrictModel, PlaceFilterVisualizationConfig>;

const initialSelectedValues: PlaceFilterResult = {
    regioner: [],
    omraader: [],
    afdelinger: [],
    praksiser: [],
    sygehuse: [],
    allRegionsSelected: false,
};

const defaultVisualizationConfig: PlaceFilterVisualizationConfig = {
    showRegioner: true,
    showOmraader: true,
    showPraksiser: true,
    showSygehuse: true,
    showAfdelinger: true,
}

const getInitialPlaceFilterSelectedValuesViaPermissions = (directUserPermissions: DirectUserPermissions) => {
    const permissionsSingleRegion = directUserPermissions.regionIds.length === 1;
    const permissionsSingleOmraade = directUserPermissions.omraadeIds.length === 1;
    const permissionsSinglePraksis = directUserPermissions.praksisIds.length === 1;
    const permissionsSingleSygehus = directUserPermissions.sygehusIds.length === 1;
    const permissionsSingleAfdeling = directUserPermissions.afdelingIds.length === 1;

    const hasOmraaderOrPraksisPermissions = directUserPermissions.omraadeIds.length > 0 || directUserPermissions.praksisIds.length > 0;
    const hasSygehusOrAfdelingPermissions = directUserPermissions.sygehusIds.length > 0 || directUserPermissions.afdelingIds.length > 0;

    const shouldPreselectRegioner = permissionsSingleRegion;
    const shouldPreselectOmraader = permissionsSingleOmraade && !hasSygehusOrAfdelingPermissions;
    const shouldPreselectPraksis = permissionsSinglePraksis && permissionsSingleOmraade && !hasSygehusOrAfdelingPermissions;
    const shouldPreselectSygehuse = permissionsSingleSygehus && !hasOmraaderOrPraksisPermissions;
    const shouldPreselectAfdelinger = permissionsSingleAfdeling && permissionsSingleSygehus && !hasOmraaderOrPraksisPermissions;

    return {
        regioner: shouldPreselectRegioner ? directUserPermissions.regionIds : [],
        omraader: shouldPreselectOmraader ? directUserPermissions.omraadeIds : [],
        praksiser: shouldPreselectPraksis ? directUserPermissions.praksisIds : [],
        sygehuse: shouldPreselectSygehuse ? directUserPermissions.sygehusIds : [],
        afdelinger: shouldPreselectAfdelinger ? directUserPermissions.afdelingIds : [],
    } as PlaceFilterResult
}

const PlaceFilter = ({
    initialSelectedValues: selectedValues=initialSelectedValues,
    permissions,
    preselectViaPermissions,
    onChange,
    titled=false,
    additionalFilterTitleClasses,
    restrictModel,
    onFilterMenuCloseCallback,
    disable,
    visualizationConfig = defaultVisualizationConfig,
} : PlaceFilterProps) => {

    const { stamdataOmraadeApi, stamdataLaegeinstitutionApi, stamdataAfdelingApi } = useStamdataApi();

    const [filter, setFilter] = useState<PlaceFilterResult>(preselectViaPermissions ? getInitialPlaceFilterSelectedValuesViaPermissions(permissions.directUserPermissions) : selectedValues);

    useEffect(() => {
        onChange(filter);
    }, [filter, onChange]);

    // Fix so that result isn't calculated every time - would trigger useEffects every time, even though permissions hadn't changed:
    const regionerPermissions = useMemo(() => mapUserPermissionsToDropdownPermissions(permissions, p => p.regionIds), [permissions]);
    const omraadePermissions = useMemo(() => mapUserPermissionsToDropdownPermissions(permissions, p => p.omraadeIds), [permissions]);
    const praksisPermissions = useMemo(() => mapUserPermissionsToDropdownPermissions(permissions, p => p.praksisIds), [permissions]);
    const sygehusePermissions = useMemo(() => mapUserPermissionsToDropdownPermissions(permissions, p => p.sygehusIds), [permissions]);
    const afdelingPermissions = useMemo(() => mapUserPermissionsToDropdownPermissions(permissions, p => p.afdelingIds), [permissions]);

    return (
        <div>
            {titled &&
                <FilterTitle title={Localizer.global_sted()} additionalClasses={additionalFilterTitleClasses}/>
            }

            {visualizationConfig.showRegioner &&
                <RegionerMultiDropdown 
                    onChangeCallback={newValue => setFilter(prev => ({...prev, ...{
                        regioner: newValue.regionIds ?? [],
                        allRegionsSelected: newValue.allRegions
                    }}))} 
                    initialSelectedOption={filter.regioner}
                    title={Localizer.forloebOverviewPage_region()}
                    placeholder={Localizer.forloebOverviewPage_chooseRegions()}
                    permissions={regionerPermissions}
                    restrictedValues={restrictModel?.regioner}
                    disable={disable}
                    onMenuCloseCallback={onFilterMenuCloseCallback}
                />
            }

            {visualizationConfig.showOmraader &&
                <ForSelectDropdown 
                    onChangeCallback={newValue => setFilter(prev => ({...prev, omraader: newValue.map(x => x.value)}))} 
                    apiFetchPromise={() => stamdataOmraadeApi.getOmraaderSelect()}
                    initialSelectedOption={filter.omraader}
                    title={Localizer.forloebOverviewPage_omraade()}
                    placeholder={Localizer.forloebOverviewPage_chooseOmraade()}
                    permissions={omraadePermissions}
                    restrictedValues={restrictModel?.omraader}
                    disable={disable}
                    onMenuCloseCallback={onFilterMenuCloseCallback}
                    isMulti
                />
            }

            {visualizationConfig.showPraksiser &&
                <ForSelectDropdown 
                    onChangeCallback={newValue => setFilter(prev => ({...prev, praksiser: newValue.map(x => x.value)}))} 
                    apiFetchPromise={() => stamdataLaegeinstitutionApi.getPraksisSelect()}
                    initialSelectedOption={filter.praksiser}
                    title={Localizer.forloebOverviewPage_praksis()}
                    placeholder={Localizer.forloebOverviewPage_choosePraksis()}
                    permissions={praksisPermissions}
                    restrictedValues={restrictModel?.praksiser}
                    disable={disable}
                    onMenuCloseCallback={onFilterMenuCloseCallback}
                    isMulti
                />
            }

            {visualizationConfig.showSygehuse &&
                <ForSelectDropdown 
                    onChangeCallback={newValue => setFilter(prev => ({...prev, sygehuse: newValue.map(x => x.value)}))} 
                    apiFetchPromise={() => stamdataLaegeinstitutionApi.getAllSygehuseSelect()}
                    initialSelectedOption={filter.sygehuse}
                    title={Localizer.forloebOverviewPage_sygehus()}
                    placeholder={Localizer.forloebOverviewPage_chooseSygehus()}
                    permissions={sygehusePermissions}
                    restrictedValues={restrictModel?.sygehuse}
                    disable={disable}
                    onMenuCloseCallback={onFilterMenuCloseCallback}
                    isMulti
                />
            }

            {visualizationConfig.showAfdelinger &&
                <ForSelectDropdown 
                    onChangeCallback={newValue => setFilter(prev => ({...prev, afdelinger: newValue.map(x => x.value)}))} 
                    apiFetchPromise={() => stamdataAfdelingApi.getAllAfdelingerSelect()}
                    initialSelectedOption={filter.afdelinger}
                    title={Localizer.forloebOverviewPage_afdeling()}
                    placeholder={Localizer.forloebOverviewPage_chooseAfdeling()}
                    permissions={afdelingPermissions}
                    restrictedValues={restrictModel?.afdelinger}
                    disable={disable}
                    onMenuCloseCallback={onFilterMenuCloseCallback}
                    isMulti
                />
            }

        </div>
    );
}

export default PlaceFilter;